import { createRouter, createWebHistory } from "vue-router";
import { ROUTE_NAMES } from "../Constants/routes";
import { useAppState } from "../Stores/AppState";
import { useUserStore } from "../Stores/User";
import { useWorkspace } from "../Stores/Workspaces";
import { useTeamPanel } from "../Stores/TeamPanel";
import { useChat } from "../Components/Chat/Store/Chat";

import { handleWorkEntityNavigation } from "./guards/workEntityGuard";
import { RouteHelper } from "../Helpers/RouteHelper";
import RefreshDetector from "../Utilities/RefreshDetector";
import Utilities from "../Helpers/utilities";

import MobileLayout from "../Mobile/Layouts/MobileLayout.vue";
import MobileAnalyticsTracker from "../Utilities/MobileAnalyticsTracker";
import { MOBILE_ANALYTICS_TYPES } from "../Constants/menus";

// Lazy load components for better performance
const Dashboard = () => import("../Components/Dashboard/Index.vue");
const Workspaces = () => import("../Components/Workspace/Index.vue");
const ActivityPages = () => import("../Components/Activity/Index.vue");
const Team = () => import("../Components/Team/Index.vue");
const UserSettings = () => import("../Components/Settings/User/Index.vue");
const Directory = () => import("../Components/Directory/Index.vue");
const Reports = () => import("../Components/Reports/Index.vue");
const Store = () => import("../Components/Store/Index.vue");
const MainLayout = () => import("../Page/MainLayout.vue");
const DesktopIndex = () => import("../Page/Desktop/Index.vue");
const Division = () => import("../Page/Division/DivisionIndex.vue");

// Helper function to check if a token is a regular route
/**
 * Saved active_page uses route/module names (e.g. division), which are not always
 * the same as the URL segment (organization → /organization).
 */
function pathForSavedActivePage(activePage) {
    if (activePage === ROUTE_NAMES.DIVISION) {
        return "/organization";
    }

    return `/${activePage}`;
}

function isRegularRoute(token) {
    const regularRoutes = [
        "work",
        "activity",
        "team",
        "quick",
        "align",
        "reports",
        "directory",
        "settings",
        "store",
        "signin",
        "signup",
        "invitations",
        "division",
        "dashboard",
    ];
    return regularRoutes.includes(token.toLowerCase());
}

const mobileRoutes = [
    ROUTE_NAMES.MOBILE,
    ROUTE_NAMES.MOBILE_ALIGN,
    ROUTE_NAMES.MOBILE_CREATE_PERSONAL_PROFILE,
    ROUTE_NAMES.MOBILE_CREATE_ORGANIZATION,
    ROUTE_NAMES.MOBILE_INVITE_TEAM,
    ROUTE_NAMES.MOBILE_BUILD_PROJECTS,
    ROUTE_NAMES.MOBILE_KNOW_KAAMFU,
    ROUTE_NAMES.MOBILE_GET_HELP,
    ROUTE_NAMES.MOBILE_WORKDAY,
    ROUTE_NAMES.MOBILE_WORK,
    ROUTE_NAMES.MOBILE_TEAM,
    ROUTE_NAMES.MOBILE_ACTIVITY,
    ROUTE_NAMES.MOBILE_DIRECTORY,
    ROUTE_NAMES.MOBILE_REPORTS,
    ROUTE_NAMES.MOBILE_CONVERSATION,
    ROUTE_NAMES.MOBILE_KAI,
    ROUTE_NAMES.MOBILE_DASHBOARD,
    ROUTE_NAMES.MOBILE_USER_PROFILE,
];

/**
 * Helper function to redirect to active work entity if available
 * Used by both root route and /work route guards
 * @param {Object} to - Vue Router route object
 * @param {Function} next - Vue Router next function
 * @returns {boolean} - True if redirect occurred, false otherwise
 */
function redirectToActiveWorkEntity(to, next) {
    const workspaceStore = useWorkspace();

    // Use last_clicked_entity for routing (stores latest clicked entity)
    const activeEntity = workspaceStore.last_clicked_entity?.entity;

    if (activeEntity?.sequence_number) {
        // Redirect to entity URL using RouteHelper
        const url = RouteHelper.getEntityUrl(activeEntity);
        if (url && to.path !== url) {
            // Only redirect if we're not already on the target URL
            // Use replace to avoid adding redirect to browser history
            next({ path: url, replace: true });
            return true;
        }
    }

    return false;
}

function trackMobileRouteNavigation(to) {
    const fullUrl = `${window.location.origin}${to.fullPath}`;
    const payload = {
        type: MOBILE_ANALYTICS_TYPES.URL_TRACKING_MOBILE,
        data: {
            url: fullUrl,
        },
    };
    MobileAnalyticsTracker.track(payload);
}

/**
 * Worker profile to use for `/team` and `/team/dashboard` when the URL has no
 * `:userProfileId` (same precedence as the Team panel).
 *
 * Must stay synchronous: {@link RedirectOption} callbacks are resolved without
 * awaiting Promises — async redirects yield "Invalid redirect" in vue-router.
 */
function resolveDefaultTeamProfileId() {
    const teamPanelStore = useTeamPanel();
    const appChat = useChat();
    const userStore = useUserStore();

    const currentProfileId =
        userStore.profile?.user_profile_id ?? userStore.profile?.id ?? null;

    return (
        appChat.standalone_associate_profile_id ??
        currentProfileId ??
        teamPanelStore.selected_member?.user_profile_id ??
        teamPanelStore.profiles_with_tasks?.[0]?.user_profile_id ??
        null
    );
}

const REMEMBERED_TEAM_ROUTE_NAMES = new Set([
    ROUTE_NAMES.TEAM_DASHBOARD,
    ROUTE_NAMES.TEAM_DOWNLINE_DASHBOARD,
    ROUTE_NAMES.TEAM_USER_WORK_MODE,
    ROUTE_NAMES.TEAM_DOWNLINE_WORK_MODE,
]);

function rememberedTeamRouteLocation(to, from) {
    if (!from?.name) {
        return null;
    }

    // Entering Team from Work/Activity/Directory/etc. always uses the root backlog URL.
    if (from.meta?.module !== "team") {
        return null;
    }

    const appStateStore = useAppState();
    const rememberedPath = appStateStore.last_team_route_path;

    if (!rememberedPath || rememberedPath === to.fullPath) {
        return null;
    }

    return {
        path: rememberedPath,
    };
}

async function defaultTeamWorkModeLocation(to) {
    const userStore = useUserStore();

    try {
        await userStore.initialize();
    } catch (error) {
        console.error("Error during app initialization:", error);
        return {
            path: "/work",
            query: to.query,
        };
    }

    if (!userStore.isOnboardingComplete) {
        return {
            name: ROUTE_NAMES.ALIGN,
        };
    }

    const id = resolveDefaultTeamProfileId();
    if (id == null || id === "") {
        return {
            path: "/work",
            query: to.query,
        };
    }

    return {
        name: ROUTE_NAMES.TEAM_DOWNLINE_WORK_MODE,
        params: {
            assignerProfileId: String(id),
            downlineProfileIds: [String(id)],
            teamWorkMode: "backlog",
        },
        query: to.query,
    };
}

const routes = [
    // Root route with MainLayout as the top-level layout
    {
        path: "/",
        component: MainLayout,
        children: [
            // Default layout route with all main app routes as children
            {
                path: "",
                component: DesktopIndex,
                children: [
                    // Home route with dynamic redirect based on saved state
                    {
                        path: "",
                        name: ROUTE_NAMES.HOME,
                        beforeEnter: async (to, from, next) => {
                            const appStateStore = useAppState();

                            // Ensure user is initialized (this will load saved state)
                            const userStore = useUserStore();
                            await userStore.initialize();

                            // Get the active_page from the restored state
                            const activePage = appStateStore.active_page;

                            // If user was on work module, check for active entity
                            if (activePage === "work") {
                                // Ensure space/board/item data is loaded
                                await appStateStore.restoreSpaceState();

                                // Try to redirect to active entity, otherwise go to /work
                                if (!redirectToActiveWorkEntity(to, next)) {
                                    next({ path: "/work", replace: true });
                                }
                                return;
                            }

                            // For other modules (team, activity, etc.), redirect to that module
                            if (activePage && activePage !== "work") {
                                next({
                                    path: pathForSavedActivePage(activePage),
                                    replace: true,
                                });
                                return;
                            }

                            // Fallback to /work if no active_page or unknown module
                            next({ path: "/work", replace: true });
                        },
                    },

                    // Main application routes
                    {
                        path: "work",
                        name: ROUTE_NAMES.WORK,
                        component: Workspaces,
                        meta: {
                            module: "work",
                            title: "Work",
                            icon: "mdi-account",
                        },
                        beforeEnter: async (to, from, next) => {
                            // Check if there's an active entity and redirect to its URL
                            if (!redirectToActiveWorkEntity(to, next)) {
                                // No active entity or already on correct URL, proceed to /work
                                next();
                            }
                        },
                    },
                    {
                        path: "work/:entityIdentifier",
                        name: ROUTE_NAMES.WORK_ENTITY,
                        component: Workspaces,
                        meta: {
                            module: "work",
                            title: "Work",
                            icon: "mdi-account",
                        },
                    },
                    {
                        path: "activity",
                        name: ROUTE_NAMES.ACTIVITY,
                        component: ActivityPages,
                        meta: {
                            module: "activity",
                            title: "Activity",
                            icon: "mdi-home-city",
                        },
                    },
                    {
                        path: "team",
                        name: ROUTE_NAMES.TEAM,
                        component: Team,
                        beforeEnter: async (to, from) => {
                            const rememberedLocation =
                                rememberedTeamRouteLocation(to, from);
                            if (rememberedLocation) {
                                return rememberedLocation;
                            }

                            return defaultTeamWorkModeLocation(to);
                        },
                        meta: {
                            module: "team",
                            title: "Team",
                            icon: "mdi-account-group-outline",
                        },
                    },
                    /** Do not bind `dashboard` as :userProfileId */
                    {
                        path: "team/dashboard",
                        redirect: (to) => {
                            const id = resolveDefaultTeamProfileId();
                            if (id == null || id === "") {
                                return {
                                    path: "/work",
                                    query: to.query,
                                };
                            }
                            return {
                                name: ROUTE_NAMES.TEAM_DOWNLINE_DASHBOARD,
                                params: {
                                    assignerProfileId: String(id),
                                    downlineProfileIds: [String(id)],
                                },
                                query: to.query,
                            };
                        },
                    },
                    {
                        path: "team/:assignerProfileId/:downlineProfileIds+/dashboard",
                        name: ROUTE_NAMES.TEAM_DOWNLINE_DASHBOARD,
                        component: Team,
                        meta: {
                            module: "team",
                            title: "Team",
                            icon: "mdi-account-group-outline",
                        },
                    },
                    {
                        path: "team/:userProfileId/dashboard",
                        redirect: (to) => ({
                            name: ROUTE_NAMES.TEAM_DOWNLINE_DASHBOARD,
                            params: {
                                assignerProfileId: to.params.userProfileId,
                                downlineProfileIds: [
                                    String(to.params.userProfileId),
                                ],
                            },
                            query: to.query,
                        }),
                    },
                    // /team/:profileId → Kanban default (same as backlog tab)
                    {
                        path: "team/:userProfileId",
                        redirect: (to) => ({
                            name: ROUTE_NAMES.TEAM_DOWNLINE_WORK_MODE,
                            params: {
                                assignerProfileId: to.params.userProfileId,
                                downlineProfileIds: [
                                    String(to.params.userProfileId),
                                ],
                                teamWorkMode: "backlog",
                            },
                            query: to.query,
                        }),
                    },
                    {
                        path: "team/:assignerProfileId/:downlineProfileIds+/:teamWorkMode(stories|screenshots|backlog|workjournal)",
                        name: ROUTE_NAMES.TEAM_DOWNLINE_WORK_MODE,
                        component: Team,
                        meta: {
                            module: "team",
                            title: "Team",
                            icon: "mdi-account-group-outline",
                        },
                    },
                    {
                        path: "team/:userProfileId/:teamWorkMode(stories|screenshots|backlog|workjournal)",
                        name: ROUTE_NAMES.TEAM_USER_WORK_MODE,
                        component: Team,
                        meta: {
                            module: "team",
                            title: "Team",
                            icon: "mdi-account-group-outline",
                        },
                    },
                    {
                        path: "quick",
                        name: ROUTE_NAMES.QUICK,
                        component: Workspaces, // Quick actions are handled within Workspaces
                        meta: {
                            module: "quick",
                            title: "Quick",
                            icon: "mdi-account",
                        },
                    },
                    {
                        path: "align",
                        name: ROUTE_NAMES.ALIGN,
                        component: () =>
                            import("../Components/Align/AlignWrapper.vue"),
                        meta: {
                            module: "align",
                            title: "Align",
                            icon: "sym_r_checklist",
                        },
                    },
                    {
                        path: "reports",
                        name: ROUTE_NAMES.REPORTS,
                        component: Reports,
                        meta: {
                            module: "reports",
                            title: "Reports",
                            icon: "sym_r_finance",
                            requiresOwnerOrAdmin: true,
                        },
                        beforeEnter: async (to, from, next) => {
                            const userStore = useUserStore();
                            const { showErrorSnackbar } = Utilities();

                            try {
                                // Ensure user is initialized
                                await userStore.initialize();

                                // If onboarding not complete, redirect to /align
                                if (!userStore.isOnboardingComplete) {
                                    next("/align");
                                    return;
                                }

                                // Check if user is owner or admin
                                if (!userStore.isOwnerOrAdmin) {
                                    // Show error toast
                                    showErrorSnackbar("Permission denied.");
                                    // Redirect to work
                                    next("/work");
                                    return;
                                }

                                next();
                            } catch (error) {
                                console.error(
                                    "Authentication error for reports:",
                                    error,
                                );
                                showErrorSnackbar(
                                    "Unable to verify your permissions. Please try again.",
                                );
                                next("/work");
                            }
                        },
                    },
                    {
                        path: "directory",
                        name: ROUTE_NAMES.DIRECTORY,
                        component: Directory,
                        meta: {
                            module: "directory",
                            title: "Directory",
                            icon: "sym_r_group",
                            requiresOwnerOrAdmin: true,
                        },
                        beforeEnter: async (to, from, next) => {
                            const userStore = useUserStore();
                            const { showErrorSnackbar } = Utilities();

                            try {
                                // Ensure user is initialized
                                await userStore.initialize();

                                if (!userStore.isOnboardingComplete) {
                                    // User is on /align route and trying to navigate away - prevent it
                                    next("/align"); // Prevent navigation
                                    return;
                                }

                                // Check if user is owner or admin
                                if (!userStore.isOwnerOrAdmin) {
                                    // Show error toast
                                    showErrorSnackbar("Permission denied.");
                                    // Redirect to work
                                    next("/work");
                                    return;
                                }

                                next();
                            } catch (error) {
                                console.error(
                                    "Authentication error for directory:",
                                    error,
                                );
                                showErrorSnackbar(
                                    "Unable to verify your permissions. Please try again.",
                                );
                                next("/work");
                            }
                        },
                    },
                    {
                        path: "organization",
                        name: ROUTE_NAMES.DIVISION,
                        component: Division,
                        meta: {
                            module: "division",
                            title: "Organization",
                            icon: "account_tree",
                        },
                    },
                    {
                        path: "settings",
                        name: ROUTE_NAMES.SETTINGS,
                        component: UserSettings,
                        meta: {
                            module: "settings",
                            title: "Settings",
                            icon: "mdi-account-settings-outline",
                        },
                    },
                    {
                        path: "store",
                        name: ROUTE_NAMES.STORE,
                        component: Store,
                        meta: {
                            module: "store",
                            title: "Store",
                            icon: "mdi-store",
                        },
                    },
                    {
                        path: "dashboard",
                        name: ROUTE_NAMES.DASHBOARD,
                        component: Dashboard,
                        meta: {
                            module: "dashboard",
                            title: "Dashboard",
                            icon: "mdi-view-dashboard",
                        },
                    },

                    {
                        path: "invitations",
                        name: ROUTE_NAMES.INVITATION,
                        component: Workspaces,
                        meta: {
                            module: "work",
                            title: "Work",
                            icon: "mdi-account",
                        },
                    },

                    // Redirect panel routes with identifiers to base routes
                    // Handles URLs like /activity/KAAM-123, /team/ABC-456, etc.
                    {
                        path: ":regularRoute(activity|team|align|reports|directory|division|settings|store)/:pathMatch(.*)*",
                        redirect: (to) => `/${to.params.regularRoute}`,
                    },

                    // Dynamic route for entity shortcuts
                    // This route will handle URLs like /1qfglayyun
                    {
                        path: ":token",
                        name: "entity-shortcut",
                        component: Workspaces, // Default to Workspaces component
                        meta: {
                            module: "work",
                            title: "Work",
                            icon: "mdi-account",
                        },
                        beforeEnter: async (to, from, next) => {
                            const token = to.params.token;

                            // Check if this looks like a valid token (not a regular route)
                            if (token && !isRegularRoute(token)) {
                                // This is a shared link token
                                try {
                                    // Ensure app is fully initialized
                                    const userStore = useUserStore();
                                    await userStore.initialize();

                                    // Use the existing handleSharedLink method
                                    const appStateStore = useAppState();
                                    const fullUrl = `${window.location.origin}/${token}`;
                                    await appStateStore.handleSharedLink(
                                        fullUrl,
                                        () => {
                                            throw new Error("Not a valid link");
                                        },
                                    );

                                    // Stay on the current route - no redirect needed
                                    next({ name: ROUTE_NAMES.WORK });
                                } catch (error) {
                                    console.error(
                                        "Shared link navigation error:",
                                        error,
                                    );
                                    next({ name: ROUTE_NAMES.WORK });
                                }
                            } else next({ name: ROUTE_NAMES.WORK });
                        },
                    },
                ],
            },
            // Mobile route
            {
                // do not lazy load the layout
                path: "mobile",
                name: ROUTE_NAMES.MOBILE,
                redirect: { name: ROUTE_NAMES.MOBILE_ALIGN },
                component: MobileLayout,
                children: [
                    {
                        path: "align",
                        redirect: { name: ROUTE_NAMES.MOBILE_ALIGN },
                        component: () =>
                            import("../Mobile/Layouts/AlignLayoutWrapper.vue"),
                        children: [
                            {
                                path: "",
                                name: ROUTE_NAMES.MOBILE_ALIGN,
                                component: () =>
                                    import(
                                        "../Page/Mobile/Align/AlignIndex.vue"
                                    ),
                            },
                            {
                                path: "create-personal-profile",
                                name: ROUTE_NAMES.MOBILE_CREATE_PERSONAL_PROFILE,
                                component: () =>
                                    import(
                                        "../Page/Mobile/Align/CreatePersonalProfileIndex.vue"
                                    ),
                            },
                            {
                                path: "create-organization",
                                name: ROUTE_NAMES.MOBILE_CREATE_ORGANIZATION,
                                component: () =>
                                    import(
                                        "../Page/Mobile/Align/CreateOrganizationIndex.vue"
                                    ),
                            },
                            {
                                path: "invite-team",
                                name: ROUTE_NAMES.MOBILE_INVITE_TEAM,
                                component: () =>
                                    import(
                                        "../Page/Mobile/Align/InviteTeamIndex.vue"
                                    ),
                            },
                            {
                                path: "build-projects",
                                name: ROUTE_NAMES.MOBILE_BUILD_PROJECTS,
                                component: () =>
                                    import(
                                        "../Page/Mobile/Align/BuildProjectsIndex.vue"
                                    ),
                            },
                            {
                                path: "know-kaamfu",
                                name: ROUTE_NAMES.MOBILE_KNOW_KAAMFU,
                                component: () =>
                                    import(
                                        "../Page/Mobile/Align/KnowKaamfuIndex.vue"
                                    ),
                            },
                            {
                                path: "get-help",
                                name: ROUTE_NAMES.MOBILE_GET_HELP,
                                component: () =>
                                    import(
                                        "../Page/Mobile/Align/GetHelpIndex.vue"
                                    ),
                            },
                        ],
                    },
                    {
                        path: "workday",
                        name: ROUTE_NAMES.MOBILE_WORKDAY,
                        component: () =>
                            import("../Page/Mobile/Workday/WorkdayIndex.vue"),
                    },
                    {
                        path: "work",
                        name: ROUTE_NAMES.MOBILE_WORK,
                        component: () =>
                            import("../Page/Mobile/Work/WorkIndex.vue"),
                    },
                    {
                        path: "team",
                        name: ROUTE_NAMES.MOBILE_TEAM,
                        component: () =>
                            import("../Page/Mobile/Team/TeamIndex.vue"),
                    },
                    {
                        path: "activity",
                        name: ROUTE_NAMES.MOBILE_ACTIVITY,
                        component: () =>
                            import("../Page/Mobile/Activity/ActivityIndex.vue"),
                    },
                    {
                        path: "directory",
                        name: ROUTE_NAMES.MOBILE_DIRECTORY,
                        component: () =>
                            import(
                                "../Page/Mobile/Directory/DirectoryIndex.vue"
                            ),
                    },
                    {
                        path: "reports",
                        name: ROUTE_NAMES.MOBILE_REPORTS,
                        component: () =>
                            import("../Page/Mobile/Reports/ReportsIndex.vue"),
                    },
                    {
                        path: "conversation",
                        name: ROUTE_NAMES.MOBILE_CONVERSATION,
                        component: () =>
                            import(
                                "../Page/Mobile/Conversation/ConversationIndex.vue"
                            ),
                    },
                    {
                        path: "kai",
                        name: ROUTE_NAMES.MOBILE_KAI,
                        component: () =>
                            import("../Page/Mobile/Kai/KaiIndex.vue"),
                    },
                    {
                        path: "dashboard",
                        name: ROUTE_NAMES.MOBILE_DASHBOARD,
                        component: () =>
                            import(
                                "../Page/Mobile/Dashboard/DashboardIndex.vue"
                            ),
                    },
                    {
                        path: "user-profile/:encodedId",
                        name: ROUTE_NAMES.MOBILE_USER_PROFILE,
                        component: () =>
                            import(
                                "../Page/Mobile/AssociateDashboard/AssociateDashboardIndex.vue"
                            ),
                    },
                ],
            },
        ],
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
    // Check if user is on mobile device and redirect to mobile route

    if (mobileRoutes.includes(to.name)) {
        trackMobileRouteNavigation(to);
        return next();
    }
    const workspaceStore = useWorkspace();
    const userStore = useUserStore();
    // When navigating to show Importer (e.g. from Operations import entry),
    // stay on /work so Spaces + Import subpanel are shown
    if (workspaceStore.import_workspace_mode) {
        next();
        return;
    }

    // Remove hash fragments from URLs (e.g., /work#hash -> /work, /activity#invalidhash -> /activity)
    if (to.hash) {
        // Redirect to same path without hash but preserve query parameters
        return next({
            path: to.path,
            query: to.query,
            // Use replace to avoid adding redirect to browser history
            replace: true,
        });
    }

    if (!to.name) {
        const normalizedPath =
            typeof to.path === "string"
                ? to.path.replace(/\/+$/, "") || "/"
                : "";
        const isUnnamedTeamEntry =
            normalizedPath === "/team" ||
            normalizedPath === "/team/dashboard";
        if (!isUnnamedTeamEntry) {
            return next("/work");
        }
        try {
            await userStore.initialize();
        } catch (error) {
            console.error("Error during app initialization:", error);
            return next("/work");
        }
        if (!userStore.isOnboardingComplete) {
            return next({ name: ROUTE_NAMES.ALIGN });
        }
        updateSelectedModule(to);
        return next();
    }

    // Handle /work/:entityIdentifier route with dedicated guard
    if (to.name === ROUTE_NAMES.WORK_ENTITY) {
        updateSelectedModule(to);
        await handleWorkEntityNavigation(to, from, next);
        return;
    }

    if (
        [
            ROUTE_NAMES.TEAM,
            ROUTE_NAMES.TEAM_DASHBOARD,
            ROUTE_NAMES.TEAM_DOWNLINE_DASHBOARD,
            ROUTE_NAMES.TEAM_USER_WORK_MODE,
            ROUTE_NAMES.TEAM_DOWNLINE_WORK_MODE,
        ].includes(to.name)
    ) {
        try {
            await userStore.initialize();
        } catch (error) {
            console.error("Error during app initialization:", error);
            return next("/work");
        }
        if (!userStore.isOnboardingComplete) {
            return next({ name: ROUTE_NAMES.ALIGN });
        }
        updateSelectedModule(to);
        next();
        return;
    }

    try {
        // Ensure app is initialized (this will be fast if already initialized)
        await userStore.initialize();

        // If user data is not loaded yet OR onboarding is not complete, and user is already on /align route, prevent navigation to other routes
        // Exception: allow mobile routes
        if (!userStore.isOnboardingComplete && to.name !== ROUTE_NAMES.ALIGN) {
            // User is on /align route and trying to navigate away - prevent it
            next("/align"); // Prevent navigation
            return;
        }

        updateSelectedModule(to);

        next();
    } catch (error) {
        console.error("Error during app initialization:", error);
        // If initialization fails, redirect to login or show error
        next("/work"); // Fallback to work route
    }
});

function updateSelectedModule(to) {
    const appStateStore = useAppState();
    if (REMEMBERED_TEAM_ROUTE_NAMES.has(to.name)) {
        appStateStore.last_team_route_path = to.fullPath;
    }

    if (to.meta.module) {
        const moduleData = appStateStore.main_modules.find(
            (mod) => mod.name === to.meta.module,
        );

        if (moduleData) {
            appStateStore.setSelectedModule(moduleData);
        }
    }
}

/**
 * Check if the current route should skip state restoration
 * These are routes where users expect a fresh state, not restored previous state
 *
 * @param {Object} route - Vue Router route object (optional, defaults to current route)
 * @returns {boolean} True if state restoration should be skipped
 */
export function shouldSkipStateRestoration(route = null) {
    // ALWAYS restore state on page refresh
    if (RefreshDetector.isPageRefresh()) {
        console.log("Page refresh detected - restoring state");
        return false; // Don't skip, restore state
    }

    // Routes that should skip state restoration (direct navigation only)
    const skipStateRestorationRoutes = [
        "/activity", // Direct activity page access
        "/work", // Direct work page access
        "/team", // Direct team page access
        /^\/team\/[^/]+(\/[^/]+)*\/dashboard/, // Team panel dashboard (incl. downline chain)
        /^\/team\/[^/]+\/(?:[^/]+\/)+(?:stories|screenshots|backlog|workjournal)$/, // Root + 1+ downline + tab
        /^\/team\/[^/]+(?:\/(stories|screenshots|backlog|workjournal))?$/, // Member or member + work tab
        "/reports", // Direct reports page access
        "/directory", // Direct directory page access
        "/division", // Direct division page access
        "/settings", // Direct settings page access
        "/store", // Direct store page access
        // Universal ID routes (e.g., /work/KAAM-15981) - direct entity access
        /^\/work\/[A-Z0-9]+[_-]\d+$/i,
    ];

    // Check window.location.pathname first (more reliable during early initialization)
    // This ensures entity routes are detected even before router updates
    const currentPath = window.location.pathname;

    // Check if current path matches any skip patterns
    const pathMatches = skipStateRestorationRoutes.some((pattern) => {
        if (typeof pattern === "string") {
            return currentPath === pattern;
        } else if (pattern instanceof RegExp) {
            return pattern.test(currentPath);
        }
        return false;
    });

    if (pathMatches) {
        return true;
    }

    // Fall back to router.currentRoute if router is available and path didn't match
    const currentRoute = route || router.currentRoute.value;
    return skipStateRestorationRoutes.some((pattern) => {
        if (typeof pattern === "string") {
            return currentRoute.path === pattern;
        } else if (pattern instanceof RegExp) {
            return pattern.test(currentRoute.path);
        }
        return false;
    });
}

export default router;
